import React from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const LayoutManager = () => {
  const { layoutConfig, updateLayout } = useAdmin();

  const sections = [
    {
      id: 'hero',
      name: 'Hero Section',
      description: 'Main landing section with title and call-to-action',
      controls: [
        { key: 'enabled', label: 'Show Hero Section', type: 'toggle' },
        { key: 'showCTA', label: 'Show Call-to-Action Button', type: 'toggle' }
      ]
    },
    {
      id: 'services',
      name: 'Services Section',
      description: 'Display your accounting services and expertise',
      controls: [
        { key: 'enabled', label: 'Show Services Section', type: 'toggle' },
        { key: 'showPricing', label: 'Show Pricing Information', type: 'toggle' }
      ]
    },
    {
      id: 'testimonials',
      name: 'Testimonials Section',
      description: 'Client reviews and success stories',
      controls: [
        { key: 'enabled', label: 'Show Testimonials', type: 'toggle' }
      ]
    },
    {
      id: 'blog',
      name: 'Blog Section',
      description: 'Latest articles and insights',
      controls: [
        { key: 'enabled', label: 'Show Blog Section', type: 'toggle' },
        { key: 'showRecent', label: 'Show Recent Posts', type: 'toggle' }
      ]
    },
    {
      id: 'contact',
      name: 'Contact Section',
      description: 'Contact information and inquiry form',
      controls: [
        { key: 'enabled', label: 'Show Contact Section', type: 'toggle' },
        { key: 'showMap', label: 'Show Google Map', type: 'toggle' },
        { key: 'showForm', label: 'Show Contact Form', type: 'toggle' }
      ]
    },
    {
      id: 'footer',
      name: 'Footer Section',
      description: 'Company information and links',
      controls: [
        { key: 'enabled', label: 'Show Footer', type: 'toggle' },
        { key: 'showSocial', label: 'Show Social Media Links', type: 'toggle' }
      ]
    }
  ];

  const handleToggle = (section, key, value) => {
    updateLayout(section, key, !value);
  };

  const resetAllSections = () => {
    if (window.confirm('Are you sure you want to reset all layout settings to defaults?')) {
      // Reset will be handled by the AdminContext resetToDefaults function
      window.location.reload();
    }
  };

  return (
    <div className="section-padding">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-naqd-light-text dark:text-naqd-dark-text mb-2">
              Layout Manager
            </h1>
            <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
              Control which sections and elements appear on your website
            </p>
          </div>

          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section.id} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-1">
                      {section.name}
                    </h3>
                    <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                      {section.description}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {layoutConfig[section.id]?.enabled ? (
                      <EyeIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <EyeSlashIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {section.controls.map((control) => (
                    <div key={control.key} className="flex items-center justify-between py-2">
                      <label className="text-sm font-medium text-naqd-light-text dark:text-naqd-dark-text">
                        {control.label}
                      </label>
                      <button
                        onClick={() => handleToggle(section.id, control.key, layoutConfig[section.id]?.[control.key])}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-naqd-primary focus:ring-offset-2 ${
                          layoutConfig[section.id]?.[control.key]
                            ? 'bg-naqd-primary'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            layoutConfig[section.id]?.[control.key]
                              ? 'translate-x-5'
                              : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-naqd-light-border dark:border-naqd-dark-border">
                  <div className="text-xs text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                    {section.id === 'hero' && layoutConfig.hero?.enabled && (
                      <span>Hero section is {layoutConfig.hero?.showCTA ? 'showing with CTA' : 'showing without CTA'}</span>
                    )}
                    {section.id === 'services' && layoutConfig.services?.enabled && (
                      <span>Services section is {layoutConfig.services?.showPricing ? 'showing with pricing' : 'showing without pricing'}</span>
                    )}
                    {section.id === 'blog' && layoutConfig.blog?.enabled && (
                      <span>Blog section is {layoutConfig.blog?.showRecent ? 'showing recent posts' : 'showing all posts'}</span>
                    )}
                    {section.id === 'contact' && layoutConfig.contact?.enabled && (
                      <span>Contact section is {layoutConfig.contact?.showMap && layoutConfig.contact?.showForm ? 'showing map and form' : 
                        layoutConfig.contact?.showMap ? 'showing map only' : 
                        layoutConfig.contact?.showForm ? 'showing form only' : 'enabled'}</span>
                    )}
                    {section.id === 'footer' && layoutConfig.footer?.enabled && (
                      <span>Footer is {layoutConfig.footer?.showSocial ? 'showing social links' : 'showing without social links'}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <div className="card bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-2">
                  Layout Preview
                </h3>
                <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-4">
                  Changes take effect immediately. Visit your website to see the updated layout.
                </p>
                <a
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-naqd-primary hover:text-naqd-primary/80 dark:text-naqd-green dark:hover:text-naqd-green/80 font-medium"
                >
                  View Website →
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={resetAllSections}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Reset All Layout Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutManager;